import { useRef, useState } from 'react';
import { ChevronDownIcon, InfoIcon, PremiumIcon } from '../../svg';

import { useFormik } from 'formik';
import Input from '../common/Input';
import useI18n from '../../hooks/useI18n';
import ToolTip from '../common/ToolTip';

export interface EditLinkFormData {
  domain: string;
  subDomain: string;
  shortUrl: string;
  tags: string;
}

interface EditLinkProps {
  isPremiumUser: boolean;
  formik: ReturnType<typeof useFormik<EditLinkFormData>>;
  isAvailable: boolean | null;
}

export const domainOptions = [
  { value: 'linkfluencer.io', label: 'linkfluencer.io', premium: false },
  { value: 'openanylink.com', label: 'openanylink.com', premium: true },
  { value: 'openbiolink.com', label: 'openbiolink.com', premium: true },
  { value: 'openmyurl.com', label: 'openmyurl.com', premium: true },
];

export default function EditLink({
  isPremiumUser,
  formik,
  isAvailable,
}: EditLinkProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { t } = useI18n();
  const [hashtagInput, setHashtagInput] = useState('');
  const hashtagInputRef = useRef<HTMLInputElement>(null);
  const isReservedSuffix = (value: string) =>
    value.trim().toLowerCase() === 'feed';
  const getHashtagsArray = () =>
    formik.values.tags ? formik.values.tags.split(' ').filter(Boolean) : [];

  const addHashtag = () => {
    const trimmed = hashtagInput.trim();
    if (!trimmed) return;

    const newHashtag = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    const updated = [...getHashtagsArray(), newHashtag];

    formik.setFieldValue('tags', updated.join(' '));
    setHashtagInput('');
  };

  const removeHashtag = (tag: string) => {
    const updated = getHashtagsArray().filter((t) => t !== tag);
    formik.setFieldValue('tags', updated.join(' '));
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Tab') {
      e.preventDefault();
      addHashtag();
    }
  };

  const handleBlur = () => {
    addHashtag();
  };

  return (
    <div className="space-y-2 md:space-y-4">
      <div className="text-[#172B4D] text-base font-semibold">
        <p></p>
      </div>
      <form onSubmit={formik.handleSubmit} className="space-y-2 md:space-y-4">
        <div className="relative">
          <Input
            id="subdomain"
            placeholder={String(t('links.prefix'))}
            type="text"
            aria-label="subdomain"
            {...formik.getFieldProps('subDomain')}
            className="!pr-8 text-black text-opacity-70"
          />
          <div className="absolute right-3 top-3">
            <ToolTip text={String(t('common.optional'))}>
              <InfoIcon className="w-5 h-5 cursor-pointer text-gray-400" />
            </ToolTip>
          </div>
        </div>

        {/* Domain dropdown */}
        <div className="relative">
          <button
            id="domain"
            aria-label="domain"
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full px-4 py-3 border border-[#B3B3B2] rounded-[32px] text-base text-black text-opacity-70 flex items-center justify-between focus:border focus:border-[#113E53]"
          >
            {formik.values.domain}
            <ChevronDownIcon stroke="#B3B3B2" />
          </button>
          {isDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white rounded-[24px] shadow-lg border border-[#B3B3B2]">
              {domainOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => {
                    formik.setFieldValue('domain', option.value);
                    // Mark the field as touched
                    formik.setFieldTouched('domain', true);
                    // Validate the entire form to trigger all validations
                    formik.validateForm().then(() => {
                      // Force a re-render by triggering setFieldValue again
                      // with the same value if needed
                      if (option.premium && !isPremiumUser) {
                        formik.setFieldValue('domain', option.value);
                      }
                    });
                    setIsDropdownOpen(false);
                  }}
                  className={`px-6 py-3 cursor-pointer flex justify-between text-[#4D494F] hover:bg-gray-100 rounded-full`}
                >
                  {option.label}
                  {option.premium && !isPremiumUser && (
                    <span className="ml-2">
                      <PremiumIcon />
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Short Key (suffix) */}
        <div>
          <Input
            id="key"
            placeholder={String(t('links.suffix'))}
            aria-label="Key"
            type="text"
            name="shortUrl"
            value={formik.values.shortUrl}
            onBlur={formik.handleBlur}
            onChange={(e) => {
              const value = e.target.value;
              formik.setFieldValue('shortUrl', value, false);

              if (isReservedSuffix(value)) {
                formik.setFieldError(
                  'shortUrl',
                  'This suffix is reserved. Please choose another.',
                );
              } else {
                formik.setFieldError('shortUrl', undefined as any);
              }
            }}
            className={`text-black text-opacity-70 ${
              formik.errors.shortUrl ? '!border-red-500' : ''
            }`}
          />
        </div>
        {formik.errors.shortUrl && (
          <div className="bg-red-500 text-white text-xs rounded-full p-2 mt-1 mr-1">
            {formik.errors.shortUrl}
          </div>
        )}
        {isAvailable === false && !formik.errors.shortUrl && (
          <div className="bg-red-500 text-white text-xs rounded-full p-2 mt-1 mr-1">
            {t('links.alreadyTaken')}
          </div>
        )}
        {isAvailable === true && !formik.errors.shortUrl && (
          <div className="bg-green-500 text-white text-xs rounded-full p-2 mt-1 mr-1">
            {t('links.available')}
          </div>
        )}

        {/* TAGS Array */}
        {/* <div>
          <Input
            id="hashtags"
            placeholder={String(t('links.hashtags'))}
            aria-label="hashtags"
            type="text"
            {...formik.getFieldProps('tags')}
            className="text-black text-opacity-70"
          />
        </div> */}
        <div>
          <div
            className={`flex flex-wrap items-center gap-2 border border-[#B3B3B2]
      ${getHashtagsArray().length >= 3 ? 'rounded-lg' : 'rounded-full'}
      py-3 px-4 min-h-[40px] w-full bg-white outline-none
      focus-within:border-[#113E53]`}
          >
            {getHashtagsArray().map((tag, index) => (
              <div
                key={index}
                className="flex items-center bg-blue-100 text-[#172B4D]
          text-sm font-medium px-2 py-1 rounded-lg"
              >
                <span>{tag}</span>
                <button
                  type="button"
                  onClick={() => removeHashtag(tag)}
                  className="ml-1 text-gray-600 hover:text-gray-800"
                >
                  <img
                    src="/assets/fi_close.svg"
                    alt="remove"
                    className="h-[16px] w-[16px]"
                  />
                </button>
              </div>
            ))}

            <input
              ref={hashtagInputRef}
              type="text"
              value={hashtagInput}
              onChange={(e) => setHashtagInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={
                getHashtagsArray().length ? '' : String(t('links.hashtags'))
              }
              className="flex-grow border-none focus:outline-none placeholder-[#B3B3B2]"
              aria-label="hashtags"
            />
          </div>
        </div>
      </form>
    </div>
  );
}
